import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Stack } from '@mui/material';

import type { SectionSlots } from '../lease-details.types';

import {
  LeaseDetailsUIProvider,
  useLeaseDetailsUI,
  type LeaseDetailsApplicantsSpacing,
  type LeaseDetailsSectionSpacing,
} from '../lease-details-ui.context';
import { SectionHeading } from '../components/section-heading';

export type SectionShellProps = {
  title?: string;
  subtitle?: string;
  slots?: SectionSlots;
  spacing?: LeaseDetailsSectionSpacing | LeaseDetailsApplicantsSpacing;
  children: ReactNode;
};

// Section frame: a canonical heading (via `SectionHeading`) plus a consistent
// rhythm between the header, slotted content (banners), and the fields.
// The section's `spacing` is republished via `LeaseDetailsUIProvider` so
// descendants (`FieldsRenderer`, `LeaseOptionBoxInput`, `ApplicantsSection`,
// …) pick up section-scoped overrides automatically.
export function SectionShell({ title, subtitle, slots, spacing, children }: SectionShellProps) {
  const ui = useLeaseDetailsUI();
  const scopedUi = useMemo(() => {
    if (spacing == null) {
      return ui;
    }
    const mergedSectionSpacing = { ...ui.sectionSpacing, ...spacing };
    const mergedApplicantsSpacing = { ...ui.applicantsSpacing, ...spacing };
    return {
      ...ui,
      sectionSpacing: mergedSectionSpacing,
      applicantsSpacing: mergedApplicantsSpacing,
    };
  }, [ui, spacing]);
  const contentSpacing = scopedUi.sectionSpacing?.content ?? 2;
  return (
    <LeaseDetailsUIProvider value={scopedUi}>
      <Stack direction="column" spacing={contentSpacing}>
        {slots?.top}
        <SectionHeading title={title} subtitle={subtitle} />
        {slots?.beforeFields}
        {children}
        {slots?.afterFields}
        {slots?.bottom}
      </Stack>
    </LeaseDetailsUIProvider>
  );
}
